(function (root, factory) {
  const api = factory(root);
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) {
    root.CorkopiaEcosystemOrchestrator = api;
    if (root.document && !root.CorkopiaOrchestrator) {
      root.CorkopiaOrchestrator = api.createOrchestrator({
        publisher(event) {
          root.document.dispatchEvent(new CustomEvent('corkopia:orchestrator', {
            detail: event
          }));
          const status = root.document.querySelector('[data-ecosystem-workflow-status]');
          if (status && ['WORKFLOW_SUCCEEDED', 'WORKFLOW_FAILED', 'WORKFLOW_MANUAL_REVIEW_REQUIRED'].includes(event.event_name)) {
            status.textContent = event.event_name === 'WORKFLOW_SUCCEEDED'
              ? 'Corkopia finished synchronizing this change.'
              : event.event_name === 'WORKFLOW_FAILED'
                ? 'Corkopia could not synchronize this change. Your confirmed Shopify cart remains authoritative.'
                : 'Corkopia needs a manual review before this workflow can continue.';
          }
        }
      });
      api.bindBrowser(root.CorkopiaOrchestrator, root.document, root);
    }
  }
})(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict';

  const VERSION = 'ecosystem-orchestrator-v1.0.0';
  const CONTRACT_VERSION = '1.0';
  const MAX_WORKFLOWS = 100;
  const MAX_RETRIES = 3;

  const STATES = Object.freeze([
    'RECEIVED',
    'VALIDATING',
    'READY',
    'RUNNING',
    'WAITING_FOR_DEPENDENCY',
    'SUCCEEDED',
    'PARTIALLY_SUCCEEDED',
    'RETRY_SCHEDULED',
    'FAILED',
    'CANCELLED',
    'MANUAL_REVIEW_REQUIRED'
  ]);

  const COMMANDS = Object.freeze({
    ADD_PRODUCT_TO_CART: 'ADD_PRODUCT_TO_CART',
    REMOVE_PRODUCT_FROM_CART: 'REMOVE_PRODUCT_FROM_CART',
    REQUEST_PRODUCT_REPLACEMENT: 'REQUEST_PRODUCT_REPLACEMENT',
    TRANSFER_SEARCH_RESULT_TO_BUILD_STUDIO: 'TRANSFER_SEARCH_RESULT_TO_BUILD_STUDIO',
    CHANGE_BUILD_SETTINGS: 'CHANGE_BUILD_SETTINGS',
    HANDLE_INVENTORY_INVALIDATION: 'HANDLE_INVENTORY_INVALIDATION',
    SIMULATE_FUTURE_ORDER_PAID: 'SIMULATE_FUTURE_ORDER_PAID',
    REFRESH_BUILD_ADVISOR: 'REFRESH_BUILD_ADVISOR',
    REFRESH_STOREFRONT_SURFACES: 'REFRESH_STOREFRONT_SURFACES'
  });

  const EVENTS = Object.freeze({
    WORKFLOW_STARTED: 'WORKFLOW_STARTED',
    WORKFLOW_STEP_COMPLETED: 'WORKFLOW_STEP_COMPLETED',
    WORKFLOW_WAITING: 'WORKFLOW_WAITING',
    WORKFLOW_SUCCEEDED: 'WORKFLOW_SUCCEEDED',
    WORKFLOW_FAILED: 'WORKFLOW_FAILED',
    WORKFLOW_RETRY_SCHEDULED: 'WORKFLOW_RETRY_SCHEDULED',
    WORKFLOW_MANUAL_REVIEW_REQUIRED: 'WORKFLOW_MANUAL_REVIEW_REQUIRED',
    PRODUCT_ACCEPTANCE_REQUESTED: 'PRODUCT_ACCEPTANCE_REQUESTED',
    PRODUCT_ACCEPTED: 'PRODUCT_ACCEPTED',
    PRODUCT_REMOVAL_REQUESTED: 'PRODUCT_REMOVAL_REQUESTED',
    PRODUCT_REMOVED: 'PRODUCT_REMOVED',
    PRODUCT_REPLACEMENT_REQUESTED: 'PRODUCT_REPLACEMENT_REQUESTED',
    BUILD_DNA_UPDATED: 'BUILD_DNA_UPDATED',
    CANVAS_SYNCHRONIZED: 'CANVAS_SYNCHRONIZED',
    BUILD_HEALTH_RECALCULATED: 'BUILD_HEALTH_RECALCULATED',
    BUILD_TIMELINE_UPDATED: 'BUILD_TIMELINE_UPDATED',
    ADVISOR_REFRESH_REQUESTED: 'ADVISOR_REFRESH_REQUESTED',
    ADVISOR_REFRESHED: 'ADVISOR_REFRESHED',
    SEARCH_CONTEXT_TRANSFERRED: 'SEARCH_CONTEXT_TRANSFERRED',
    INVENTORY_INVALIDATED: 'INVENTORY_INVALIDATED',
    STOREFRONT_SURFACES_REFRESH_REQUESTED: 'STOREFRONT_SURFACES_REFRESH_REQUESTED',
    FUTURE_ORDER_PAID_RECEIVED: 'FUTURE_ORDER_PAID_RECEIVED',
    FUTURE_REPLENISHMENT_REQUESTED: 'FUTURE_REPLENISHMENT_REQUESTED',
    PRODUCT_ACCEPTED_WORKFLOW_COMPLETED: 'PRODUCT_ACCEPTED_WORKFLOW_COMPLETED',
    PRODUCT_REMOVED_WORKFLOW_COMPLETED: 'PRODUCT_REMOVED_WORKFLOW_COMPLETED',
    SEARCH_TO_BUILD_STUDIO_COMPLETED: 'SEARCH_TO_BUILD_STUDIO_COMPLETED',
    INVENTORY_INVALIDATION_HANDLED: 'INVENTORY_INVALIDATION_HANDLED'
  });

  const WORKFLOWS = Object.freeze({
    ADD_PRODUCT_TO_CART: 'PRODUCT_ACCEPTED',
    REMOVE_PRODUCT_FROM_CART: 'PRODUCT_REMOVED',
    REQUEST_PRODUCT_REPLACEMENT: 'PRODUCT_REPLACED',
    TRANSFER_SEARCH_RESULT_TO_BUILD_STUDIO: 'SEARCH_TO_BUILD_STUDIO',
    CHANGE_BUILD_SETTINGS: 'BUILD_SETTINGS_CHANGED',
    HANDLE_INVENTORY_INVALIDATION: 'INVENTORY_INVALIDATED',
    SIMULATE_FUTURE_ORDER_PAID: 'FUTURE_ORDER_PAID',
    REFRESH_BUILD_ADVISOR: 'ADVISOR_REFRESH',
    REFRESH_STOREFRONT_SURFACES: 'STOREFRONT_SURFACES_REFRESH'
  });

  const ACTORS = new Set(['anonymous_customer', 'customer', 'operator', 'service', 'simulator']);
  const SOURCES = new Set(['storefront', 'build_studio', 'smart_box', 'visual_build_canvas', 'search', 'intelligence_layer', 'c3', 'shopify_adapter', 'simulator']);
  const EVENT_NAMES = new Set(Object.values(EVENTS));
  const COMMAND_NAMES = new Set(Object.values(COMMANDS));
  const FORBIDDEN_KEYS = /(?:email|address|phone|payment|card|secret|token|authorization|webhook_secret|raw_query)/i;

  const text = (value, limit = 180) => String(value ?? '').trim().slice(0, limit);
  const safeId = (value) => text(value).replace(/[^a-zA-Z0-9:._/-]/g, '').slice(0, 180);
  const clone = (value) => JSON.parse(JSON.stringify(value));
  const nowIso = (clock) => new Date(clock()).toISOString();
  const hash = (value) => {
    let output = 2166136261;
    const input = String(value);
    for (let index = 0; index < input.length; index += 1) {
      output ^= input.charCodeAt(index);
      output = Math.imul(output, 16777619);
    }
    return (output >>> 0).toString(36);
  };
  const sanitizeReason = (error) => text(error?.code || error?.message || error || 'WORKFLOW_STEP_FAILED', 160)
    .replace(/[<>{}]/g, '');
  const sanitize = (value, depth = 0) => {
    if (depth > 5 || value == null) return value == null ? null : undefined;
    if (Array.isArray(value)) return value.slice(0, 60).map((item) => sanitize(item, depth + 1));
    if (typeof value === 'object') {
      return Object.fromEntries(Object.entries(value)
        .filter(([key]) => !FORBIDDEN_KEYS.test(key))
        .slice(0, 80)
        .map(([key, entry]) => [key, sanitize(entry, depth + 1)]));
    }
    if (typeof value === 'string') return text(value, 500);
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (typeof value === 'boolean') return value;
    return undefined;
  };

  const workflowError = (code, message, options = {}) => Object.assign(
    new Error(message || code),
    { code, ...options }
  );

  const requiredId = (payload, field, code = 'IDENTITY_REQUIRED') => {
    const value = safeId(payload?.[field]);
    if (!value) throw workflowError(code, `${field} is required.`);
    return value;
  };

  const validateProductIdentity = (payload) => {
    requiredId(payload, 'variantId');
    requiredId(payload, 'pieceId');
    if (payload.recommendationId != null) requiredId(payload, 'recommendationId');
    return { valid: true };
  };

  const validateCartLine = (payload) => ({
    valid: Boolean(requiredId(payload, 'cartLineKey'))
  });

  const validateSearchTransfer = (payload) => {
    requiredId(payload, 'searchDnaId');
    requiredId(payload, 'variantId');
    if (payload.autoAccept === true) throw workflowError('EXPLICIT_CONFIRMATION_REQUIRED', 'Search cannot auto-accept a product.');
    return { valid: true, cartMutation: false };
  };

  const validateSettings = (payload) => {
    if (!payload || !Object.keys(payload.settings || {}).length) {
      throw workflowError('BUILD_SETTINGS_REQUIRED', 'Build settings are required.');
    }
    return { valid: true };
  };

  const validateInventory = (payload) => {
    requiredId(payload, 'variantId');
    const reason = text(payload.reason).toUpperCase();
    if (!['SOLD', 'UNAVAILABLE', 'RESERVED', 'UNRELEASED', 'VALIDATION_FAILED'].includes(reason)) {
      throw workflowError('INVALID_INVENTORY_REASON', 'Inventory invalidation reason is not supported.');
    }
    return { valid: true };
  };

  const validateFuturePaidOrder = (payload) => {
    if (payload.simulation !== true) throw workflowError('PRODUCTION_WEBHOOK_DISABLED', 'Only the local paid-order simulator is enabled.');
    requiredId(payload, 'orderId');
    requiredId(payload, 'lineItemId');
    requiredId(payload, 'variantId');
    requiredId(payload, 'pieceId');
    if (payload.signatureVerified !== true) {
      throw workflowError('SIMULATED_SIGNATURE_REVIEW', 'The simulated signature did not verify.', { manualReview: true });
    }
    if (payload.financialStatus !== 'PAID') {
      throw workflowError('PAID_STATUS_REQUIRED', 'The simulated order is not paid.', { manualReview: true });
    }
    return { valid: true, productionExecution: false };
  };

  const step = (id, adapter, options = {}) => ({ id, adapter, ...options });

  const DEFINITIONS = Object.freeze({
    PRODUCT_ACCEPTED: {
      requestedEvent: EVENTS.PRODUCT_ACCEPTANCE_REQUESTED,
      completedEvent: EVENTS.PRODUCT_ACCEPTED_WORKFLOW_COMPLETED,
      steps: [
        step('validate_recommendation_and_identity', null, { run: ({ payload }) => validateProductIdentity(payload), critical: true }),
        step('revalidate_current_eligibility', 'revalidateEligibility', { critical: true }),
        step('stage_acceptance_ui', 'stageAcceptanceUi', { compensate: 'restoreAcceptanceUi' }),
        step('shopify_cart_add', 'cartAdd', { critical: true, authoritative: 'shopify' }),
        step('update_build_dna_accepted_history', 'updateBuildDnaAccepted', { event: EVENTS.BUILD_DNA_UPDATED }),
        step('synchronize_canvas', 'syncCanvas', { event: EVENTS.CANVAS_SYNCHRONIZED }),
        step('recalculate_build_health', 'recalculateBuildHealth', { event: EVENTS.BUILD_HEALTH_RECALCULATED }),
        step('update_build_timeline', 'updateBuildTimeline', { event: EVENTS.BUILD_TIMELINE_UPDATED }),
        step('refresh_smart_box_advisor', 'refreshSmartBoxAdvisor', { event: EVENTS.ADVISOR_REFRESHED }),
        step('refresh_next_best_piece', 'refreshNextBestPiece', { event: EVENTS.ADVISOR_REFRESHED }),
        step('emit_acceptance_analytics', 'emitAnalytics', { optional: true }),
        step('record_product_accepted', null, { run: () => ({ accepted: true }), event: EVENTS.PRODUCT_ACCEPTED })
      ]
    },
    PRODUCT_REMOVED: {
      requestedEvent: EVENTS.PRODUCT_REMOVAL_REQUESTED,
      completedEvent: EVENTS.PRODUCT_REMOVED_WORKFLOW_COMPLETED,
      steps: [
        step('identify_shopify_cart_line', null, { run: ({ payload }) => validateCartLine(payload), critical: true }),
        step('shopify_cart_remove', 'cartRemove', { critical: true, authoritative: 'shopify' }),
        step('remove_canvas_placement', 'removeCanvasPlacement', { event: EVENTS.CANVAS_SYNCHRONIZED }),
        step('update_build_dna_after_removal', 'updateBuildDnaRemoved', { event: EVENTS.BUILD_DNA_UPDATED }),
        step('recalculate_build_health', 'recalculateBuildHealth', { event: EVENTS.BUILD_HEALTH_RECALCULATED }),
        step('update_build_timeline', 'updateBuildTimeline', { event: EVENTS.BUILD_TIMELINE_UPDATED }),
        step('refresh_advisors_after_removal', 'refreshAdvisors', { event: EVENTS.ADVISOR_REFRESHED }),
        step('emit_removal_analytics', 'emitAnalytics', { optional: true }),
        step('record_product_removed', null, { run: () => ({ removed: true }), event: EVENTS.PRODUCT_REMOVED })
      ]
    },
    PRODUCT_REPLACED: {
      requestedEvent: EVENTS.PRODUCT_REPLACEMENT_REQUESTED,
      completedEvent: EVENTS.WORKFLOW_SUCCEEDED,
      steps: [
        step('validate_replacement_identity', null, { run: ({ payload }) => validateProductIdentity(payload), critical: true }),
        step('record_replacement_history', 'recordReplacementHistory', { event: EVENTS.BUILD_DNA_UPDATED }),
        step('request_same_role_recommendation', 'requestReplacementRecommendation', { event: EVENTS.ADVISOR_REFRESH_REQUESTED }),
        step('present_replacement_without_cart_mutation', 'presentReplacement', { event: EVENTS.ADVISOR_REFRESHED }),
        step('emit_replacement_analytics', 'emitAnalytics', { optional: true })
      ]
    },
    SEARCH_TO_BUILD_STUDIO: {
      requestedEvent: EVENTS.ADVISOR_REFRESH_REQUESTED,
      completedEvent: EVENTS.SEARCH_TO_BUILD_STUDIO_COMPLETED,
      steps: [
        step('validate_search_transfer', null, { run: ({ payload }) => validateSearchTransfer(payload), critical: true }),
        step('preserve_search_dna', 'preserveSearchDna'),
        step('translate_search_intent', 'translateSearchIntent'),
        step('validate_build_dna_context', 'validateBuildDnaContext'),
        step('stage_search_candidate', 'stageSearchCandidate', { event: EVENTS.SEARCH_CONTEXT_TRANSFERRED }),
        step('refresh_build_recommendations', 'refreshBuildRecommendations', { event: EVENTS.ADVISOR_REFRESHED })
      ]
    },
    BUILD_SETTINGS_CHANGED: {
      requestedEvent: EVENTS.ADVISOR_REFRESH_REQUESTED,
      completedEvent: EVENTS.WORKFLOW_SUCCEEDED,
      steps: [
        step('validate_build_settings', null, { run: ({ payload }) => validateSettings(payload), critical: true }),
        step('version_build_dna', 'versionBuildDna', { event: EVENTS.BUILD_DNA_UPDATED }),
        step('identify_valid_selected_pieces', 'identifyValidSelectedPieces'),
        step('identify_pieces_needing_review', 'identifyPiecesNeedingReview'),
        step('recalculate_build_health', 'recalculateBuildHealth', { event: EVENTS.BUILD_HEALTH_RECALCULATED }),
        step('update_build_timeline', 'updateBuildTimeline', { event: EVENTS.BUILD_TIMELINE_UPDATED }),
        step('refresh_advisors', 'refreshAdvisors', { event: EVENTS.ADVISOR_REFRESHED }),
        step('show_build_conflicts', 'showBuildConflicts')
      ]
    },
    INVENTORY_INVALIDATED: {
      requestedEvent: EVENTS.INVENTORY_INVALIDATED,
      completedEvent: EVENTS.INVENTORY_INVALIDATION_HANDLED,
      steps: [
        step('validate_inventory_invalidation', null, { run: ({ payload }) => validateInventory(payload), critical: true }),
        step('identify_affected_surfaces', 'identifyAffectedSurfaces'),
        step('revalidate_cart_membership', 'revalidateCartMembership', { critical: true, authoritative: 'shopify' }),
        step('remove_ineligible_recommendation_or_warn_cart', 'handleInvalidatedSubject'),
        step('request_current_replacement', 'requestCurrentReplacement', { event: EVENTS.PRODUCT_REPLACEMENT_REQUESTED }),
        step('refresh_storefront_surfaces', 'refreshStorefrontSurfaces', { event: EVENTS.STOREFRONT_SURFACES_REFRESH_REQUESTED }),
        step('refresh_affected_advisors', 'refreshAdvisors', { event: EVENTS.ADVISOR_REFRESHED })
      ]
    },
    FUTURE_ORDER_PAID: {
      requestedEvent: EVENTS.FUTURE_ORDER_PAID_RECEIVED,
      completedEvent: EVENTS.WORKFLOW_SUCCEEDED,
      simulatorOnly: true,
      steps: [
        step('validate_future_paid_order_simulation', null, { run: ({ payload }) => validateFuturePaidOrder(payload), critical: true }),
        step('resolve_exact_piece_identity', 'simulateExactPieceResolution', { critical: true }),
        step('simulate_c3_sold_transition', 'simulateSoldTransition'),
        step('simulate_storefront_eligibility_removal', 'simulateEligibilityRemoval', { event: EVENTS.INVENTORY_INVALIDATED }),
        step('simulate_replenishment_request', 'simulateReplenishmentRequest', { event: EVENTS.FUTURE_REPLENISHMENT_REQUESTED }),
        step('simulate_cache_invalidation', 'simulateCacheInvalidation', { event: EVENTS.STOREFRONT_SURFACES_REFRESH_REQUESTED }),
        step('notify_command_center_projection', 'notifyCommandCenter')
      ]
    },
    ADVISOR_REFRESH: {
      requestedEvent: EVENTS.ADVISOR_REFRESH_REQUESTED,
      completedEvent: EVENTS.ADVISOR_REFRESHED,
      steps: [step('refresh_requested_advisor', 'refreshAdvisors', { critical: true })]
    },
    STOREFRONT_SURFACES_REFRESH: {
      requestedEvent: EVENTS.STOREFRONT_SURFACES_REFRESH_REQUESTED,
      completedEvent: EVENTS.WORKFLOW_SUCCEEDED,
      steps: [step('refresh_requested_surfaces', 'refreshStorefrontSurfaces', { critical: true })]
    }
  });

  const businessKey = (command) => {
    const payload = command.payload || {};
    switch (command.command_name) {
      case COMMANDS.ADD_PRODUCT_TO_CART:
        return `cart-add:${safeId(payload.recommendationId || payload.requestId || command.command_id)}:${safeId(payload.variantId)}`;
      case COMMANDS.REMOVE_PRODUCT_FROM_CART:
        return `cart-remove:${safeId(payload.cartLineKey)}`;
      case COMMANDS.REQUEST_PRODUCT_REPLACEMENT:
        return `replacement:${safeId(payload.recommendationId || payload.variantId)}:${safeId(payload.decisionSequence || 1)}`;
      case COMMANDS.TRANSFER_SEARCH_RESULT_TO_BUILD_STUDIO:
        return `search-transfer:${safeId(payload.searchDnaId)}:${safeId(payload.variantId)}`;
      case COMMANDS.CHANGE_BUILD_SETTINGS:
        return `settings:${safeId(payload.buildDnaVersion || command.command_id)}`;
      case COMMANDS.HANDLE_INVENTORY_INVALIDATION:
        return `inventory-invalidation:${safeId(payload.variantId)}:${safeId(payload.catalogGeneration || payload.reason)}`;
      case COMMANDS.SIMULATE_FUTURE_ORDER_PAID:
        return `future-paid:${safeId(payload.orderId)}:${safeId(payload.lineItemId)}:${safeId(payload.pieceId)}`;
      case COMMANDS.REFRESH_BUILD_ADVISOR:
        return `advisor-refresh:${safeId(payload.advisorId || 'build')}:${safeId(payload.reason || command.command_id)}`;
      default:
        return `surface-refresh:${safeId(payload.surface || 'all')}:${safeId(payload.catalogGeneration || command.command_id)}`;
    }
  };

  const createCommand = (commandName, payload = {}, metadata = {}) => ({
    command_version: CONTRACT_VERSION,
    command_id: safeId(metadata.commandId || `cmd_${hash(`${commandName}:${Date.now()}:${Math.random()}`)}`),
    command_name: commandName,
    issued_at: metadata.issuedAt || new Date().toISOString(),
    source_system: metadata.sourceSystem || 'storefront',
    actor: {
      type: metadata.actorType || 'anonymous_customer',
      opaque_id: metadata.opaqueId ? safeId(metadata.opaqueId) : undefined
    },
    correlation_id: safeId(metadata.correlationId || metadata.commandId || `corr_${hash(JSON.stringify(sanitize(payload)))}`),
    causation_id: metadata.causationId ? safeId(metadata.causationId) : null,
    payload: sanitize(payload)
  });

  const validateCommand = (command) => {
    const errors = [];
    if (command?.command_version !== CONTRACT_VERSION) errors.push('COMMAND_VERSION_INVALID');
    if (!COMMAND_NAMES.has(command?.command_name)) {
      errors.push(EVENT_NAMES.has(command?.command_name) ? 'EVENT_USED_AS_COMMAND' : 'COMMAND_NAME_INVALID');
    }
    if (!safeId(command?.command_id)) errors.push('COMMAND_ID_REQUIRED');
    if (!SOURCES.has(command?.source_system)) errors.push('SOURCE_SYSTEM_INVALID');
    if (!ACTORS.has(command?.actor?.type)) errors.push('ACTOR_TYPE_INVALID');
    if (!command?.payload || typeof command.payload !== 'object') errors.push('COMMAND_PAYLOAD_INVALID');
    return { valid: errors.length === 0, errors };
  };

  const validateEvent = (event) => {
    const errors = [];
    if (event?.event_version !== CONTRACT_VERSION) errors.push('EVENT_VERSION_INVALID');
    if (!EVENT_NAMES.has(event?.event_name)) {
      errors.push(COMMAND_NAMES.has(event?.event_name) ? 'COMMAND_USED_AS_EVENT' : 'EVENT_NAME_INVALID');
    }
    if (!safeId(event?.event_id)) errors.push('EVENT_ID_REQUIRED');
    if (!safeId(event?.workflow_id)) errors.push('WORKFLOW_ID_REQUIRED');
    return { valid: errors.length === 0, errors };
  };

  const createOrchestrator = (options = {}) => {
    const clock = options.clock || Date.now;
    const publisher = options.publisher || (() => true);
    const baseAdapters = { ...(options.adapters || {}) };
    const workflows = new Map();
    const idempotency = new Map();
    const emittedEvents = [];
    let duplicateEventCount = 0;
    let compensationCount = 0;

    const publish = async (workflow, eventName, payload = {}) => {
      const event = {
        event_version: CONTRACT_VERSION,
        event_id: `evt_${hash(`${workflow.workflowId}:${eventName}:${emittedEvents.length}`)}`,
        event_name: eventName,
        occurred_at: nowIso(clock),
        producer: 'corkopia_ecosystem_orchestrator',
        workflow_id: workflow.workflowId,
        workflow_type: workflow.workflowType,
        correlation_id: workflow.correlationId,
        causation_id: workflow.causationId,
        source_system: workflow.sourceSystem,
        actor: { type: workflow.actorType, opaque_id: workflow.actorOpaqueId || undefined },
        classification: 'customer_context',
        idempotency_key: `${workflow.idempotencyKey}:${eventName}`,
        payload: sanitize(payload)
      };
      const validation = validateEvent(event);
      if (!validation.valid) throw workflowError('EVENT_ENVELOPE_INVALID', validation.errors.join(','));
      if (!workflow.eventId) workflow.eventId = event.event_id;
      emittedEvents.push(event);
      await publisher(clone(event));
      return event;
    };

    const transition = (workflow, state) => {
      if (!STATES.includes(state)) throw workflowError('WORKFLOW_STATE_INVALID', state);
      workflow.currentState = state;
      workflow.timestamps.updatedAt = nowIso(clock);
      workflow.transitions.push({ state, at: workflow.timestamps.updatedAt });
    };

    const persist = (workflow) => {
      workflows.set(workflow.workflowId, workflow);
      while (workflows.size > MAX_WORKFLOWS) workflows.delete(workflows.keys().next().value);
      options.store?.set?.(workflow.workflowId, sanitize(workflow));
    };

    const compensate = async (workflow, definition, adapters, context) => {
      for (const completed of [...workflow.completedSteps].reverse()) {
        const definitionStep = definition.steps.find((candidate) => candidate.id === completed.id);
        if (!definitionStep?.compensate || definitionStep.authoritative) continue;
        const handler = adapters[definitionStep.compensate];
        if (typeof handler !== 'function') continue;
        await handler({ ...context, workflow: clone(workflow), step: definitionStep });
        workflow.compensations.push({
          step: definitionStep.id,
          handler: definitionStep.compensate,
          at: nowIso(clock)
        });
        compensationCount += 1;
      }
    };

    const executeSteps = async (workflow, definition, adapters, startIndex = 0) => {
      transition(workflow, 'RUNNING');
      await publish(workflow, EVENTS.WORKFLOW_STARTED, { retryCount: workflow.retryCount });
      const context = { payload: workflow.payload, command: workflow.command, workflow: clone(workflow) };
      for (let index = startIndex; index < definition.steps.length; index += 1) {
        const currentStep = definition.steps[index];
        if (workflow.completedSteps.some((completed) => completed.id === currentStep.id)) continue;
        workflow.activeStep = currentStep.id;
        try {
          const handler = currentStep.run || adapters[currentStep.adapter];
          if (typeof handler !== 'function') {
            if (currentStep.optional) {
              workflow.completedSteps.push({ id: currentStep.id, status: 'SKIPPED_OPTIONAL', at: nowIso(clock) });
              continue;
            }
            throw workflowError('DEPENDENCY_NOT_REGISTERED', `${currentStep.adapter} is not registered.`, { waiting: true });
          }
          const output = await handler({ ...context, workflow: clone(workflow), step: currentStep });
          if (output === false || output?.ok === false) {
            throw workflowError(output?.code || 'WORKFLOW_STEP_REJECTED', output?.reason || `${currentStep.id} was rejected.`);
          }
          workflow.completedSteps.push({ id: currentStep.id, status: 'COMPLETED', at: nowIso(clock) });
          workflow.outputs[currentStep.id] = sanitize(output);
          workflow.failedStep = null;
          workflow.errorReason = null;
          persist(workflow);
          await publish(workflow, EVENTS.WORKFLOW_STEP_COMPLETED, { step: currentStep.id });
          if (currentStep.event) await publish(workflow, currentStep.event, { step: currentStep.id });
        } catch (error) {
          workflow.failedStep = currentStep.id;
          workflow.errorReason = sanitizeReason(error);
          workflow.activeStep = null;
          if (error?.manualReview) {
            transition(workflow, 'MANUAL_REVIEW_REQUIRED');
            persist(workflow);
            await publish(workflow, EVENTS.WORKFLOW_MANUAL_REVIEW_REQUIRED, {
              failedStep: currentStep.id,
              reason: workflow.errorReason
            });
            return workflow;
          }
          if (error?.waiting) {
            transition(workflow, 'WAITING_FOR_DEPENDENCY');
            persist(workflow);
            await publish(workflow, EVENTS.WORKFLOW_WAITING, {
              failedStep: currentStep.id,
              reason: workflow.errorReason
            });
            return workflow;
          }
          const authoritativeSuccess = workflow.completedSteps.some((completed) =>
            definition.steps.find((candidate) => candidate.id === completed.id)?.authoritative
          );
          if (authoritativeSuccess && !currentStep.critical) {
            transition(workflow, 'PARTIALLY_SUCCEEDED');
            workflow.retry = {
              scheduled: workflow.retryCount < workflow.maxRetries,
              resumeAtStep: currentStep.id,
              reason: workflow.errorReason
            };
            persist(workflow);
            if (workflow.retry.scheduled) {
              await publish(workflow, EVENTS.WORKFLOW_RETRY_SCHEDULED, workflow.retry);
            }
            return workflow;
          }
          await compensate(workflow, definition, adapters, context);
          transition(workflow, 'FAILED');
          persist(workflow);
          await publish(workflow, EVENTS.WORKFLOW_FAILED, {
            failedStep: currentStep.id,
            reason: workflow.errorReason,
            compensationCount: workflow.compensations.length
          });
          return workflow;
        }
      }
      workflow.activeStep = null;
      workflow.failedStep = null;
      workflow.errorReason = null;
      workflow.result = 'COMPLETED';
      transition(workflow, 'SUCCEEDED');
      workflow.timestamps.completedAt = nowIso(clock);
      persist(workflow);
      if (definition.completedEvent && definition.completedEvent !== EVENTS.WORKFLOW_SUCCEEDED) {
        await publish(workflow, definition.completedEvent, { completedSteps: workflow.completedSteps.length });
      }
      await publish(workflow, EVENTS.WORKFLOW_SUCCEEDED, { completedSteps: workflow.completedSteps.length });
      return workflow;
    };

    const execute = async (command, adapterOverrides = {}) => {
      const validation = validateCommand(command);
      if (!validation.valid) throw workflowError('COMMAND_ENVELOPE_INVALID', validation.errors.join(','));
      const idempotencyKey = businessKey(command);
      const existingId = idempotency.get(idempotencyKey);
      if (existingId && workflows.has(existingId)) {
        duplicateEventCount += 1;
        return { ...clone(workflows.get(existingId)), duplicate: true };
      }
      const workflowType = WORKFLOWS[command.command_name];
      const definition = DEFINITIONS[workflowType];
      const workflowId = `wf_${hash(`${idempotencyKey}:${command.correlation_id}`)}`;
      const startedAt = nowIso(clock);
      const workflow = {
        workflowId,
        workflowType,
        orchestratorVersion: VERSION,
        correlationId: safeId(command.correlation_id),
        causationId: safeId(command.causation_id || command.command_id),
        eventId: null,
        commandId: safeId(command.command_id),
        command: sanitize(command),
        actorType: command.actor.type,
        actorOpaqueId: safeId(command.actor.opaque_id || ''),
        sourceSystem: command.source_system,
        currentState: 'RECEIVED',
        completedSteps: [],
        failedStep: null,
        retryCount: 0,
        maxRetries: Number.isFinite(options.maxRetries) ? options.maxRetries : MAX_RETRIES,
        timestamps: { receivedAt: startedAt, updatedAt: startedAt, completedAt: null },
        transitions: [{ state: 'RECEIVED', at: startedAt }],
        errorReason: null,
        idempotencyKey,
        payload: sanitize(command.payload),
        outputs: {},
        compensations: [],
        retry: null,
        result: null,
        productionConnectionsUsed: false
      };
      workflows.set(workflowId, workflow);
      idempotency.set(idempotencyKey, workflowId);
      transition(workflow, 'VALIDATING');
      persist(workflow);
      if (definition.requestedEvent) await publish(workflow, definition.requestedEvent, { commandId: workflow.commandId });
      transition(workflow, 'READY');
      persist(workflow);
      return clone(await executeSteps(workflow, definition, { ...baseAdapters, ...adapterOverrides }));
    };

    const retry = async (workflowId, adapterOverrides = {}) => {
      const workflow = workflows.get(workflowId);
      if (!workflow) throw workflowError('WORKFLOW_NOT_FOUND', workflowId);
      if (!['PARTIALLY_SUCCEEDED', 'WAITING_FOR_DEPENDENCY', 'RETRY_SCHEDULED', 'FAILED'].includes(workflow.currentState)) {
        return clone(workflow);
      }
      if (workflow.retryCount >= workflow.maxRetries) {
        transition(workflow, 'MANUAL_REVIEW_REQUIRED');
        workflow.errorReason = 'RETRY_LIMIT_REACHED';
        persist(workflow);
        await publish(workflow, EVENTS.WORKFLOW_MANUAL_REVIEW_REQUIRED, { reason: workflow.errorReason });
        return clone(workflow);
      }
      workflow.retryCount += 1;
      workflow.retry = null;
      const definition = DEFINITIONS[workflow.workflowType];
      const startIndex = Math.max(0, definition.steps.findIndex((candidate) => candidate.id === workflow.failedStep));
      return clone(await executeSteps(workflow, definition, { ...baseAdapters, ...adapterOverrides }, startIndex));
    };

    const cancel = async (workflowId, reason = 'CANCELLED_BY_ACTOR') => {
      const workflow = workflows.get(workflowId);
      if (!workflow) throw workflowError('WORKFLOW_NOT_FOUND', workflowId);
      if (['SUCCEEDED', 'FAILED', 'CANCELLED'].includes(workflow.currentState)) return clone(workflow);
      transition(workflow, 'CANCELLED');
      workflow.errorReason = sanitizeReason(reason);
      persist(workflow);
      return clone(workflow);
    };

    const list = (filters = {}) => [...workflows.values()]
      .filter((workflow) => !filters.state || workflow.currentState === filters.state)
      .filter((workflow) => !filters.workflowType || workflow.workflowType === filters.workflowType)
      .sort((a, b) => b.timestamps.receivedAt.localeCompare(a.timestamps.receivedAt))
      .map(clone);

    const metrics = () => {
      const rows = list();
      const completedDurations = rows
        .filter((row) => row.timestamps.completedAt)
        .map((row) => new Date(row.timestamps.completedAt).getTime() - new Date(row.timestamps.receivedAt).getTime());
      const count = rows.length;
      const succeeded = rows.filter((row) => row.currentState === 'SUCCEEDED').length;
      const failed = rows.filter((row) => row.currentState === 'FAILED').length;
      const retrying = rows.filter((row) => ['PARTIALLY_SUCCEEDED', 'RETRY_SCHEDULED', 'WAITING_FOR_DEPENDENCY'].includes(row.currentState)).length;
      return {
        workflowCount: count,
        successRate: count ? succeeded / count : 0,
        failureRate: count ? failed / count : 0,
        retryRate: count ? retrying / count : 0,
        averageDurationMs: completedDurations.length
          ? Math.round(completedDurations.reduce((sum, value) => sum + value, 0) / completedDurations.length)
          : 0,
        longestRunningWorkflowMs: rows.length
          ? Math.max(...rows.map((row) => (row.timestamps.completedAt
            ? new Date(row.timestamps.completedAt).getTime()
            : clock()) - new Date(row.timestamps.receivedAt).getTime()))
          : 0,
        duplicateEventCount,
        compensationCount,
        manualReviewCount: rows.filter((row) => row.currentState === 'MANUAL_REVIEW_REQUIRED').length
      };
    };

    const logs = () => list().map((workflow) => sanitize({
      workflowId: workflow.workflowId,
      workflowType: workflow.workflowType,
      currentState: workflow.currentState,
      correlationId: workflow.correlationId,
      causationId: workflow.causationId,
      eventId: workflow.eventId,
      actorType: workflow.actorType,
      actorOpaqueId: workflow.actorOpaqueId,
      sourceSystem: workflow.sourceSystem,
      completedSteps: workflow.completedSteps.map((entry) => entry.id),
      failedStep: workflow.failedStep,
      retryCount: workflow.retryCount,
      timestamps: workflow.timestamps,
      errorReason: workflow.errorReason,
      affectedPiece: workflow.payload.pieceId || null
    }));

    return Object.freeze({
      version: VERSION,
      execute,
      retry,
      cancel,
      list,
      metrics,
      logs,
      events: () => emittedEvents.map(clone),
      registerAdapters(adapters = {}) {
        Object.assign(baseAdapters, adapters);
        return this;
      },
      get(workflowId) {
        return workflows.has(workflowId) ? clone(workflows.get(workflowId)) : null;
      }
    });
  };

  const bindBrowser = (orchestrator, documentRef, browserRoot) => {
    if (!orchestrator || !documentRef || documentRef.documentElement?.dataset?.corkopiaOrchestratorBound === 'true') return false;
    if (documentRef.documentElement) documentRef.documentElement.dataset.corkopiaOrchestratorBound = 'true';
    documentRef.addEventListener('corkopia:piece-unavailable', (event) => {
      const variantId = safeId(event.detail?.variantId);
      if (!variantId) return;
      const pieceId = safeId(event.detail?.pieceId || `variant-${variantId}`);
      const command = createCommand(
        COMMANDS.HANDLE_INVENTORY_INVALIDATION,
        {
          variantId,
          pieceId,
          reason: 'UNAVAILABLE',
          catalogGeneration: null
        },
        {
          sourceSystem: 'shopify_adapter',
          actorType: 'service',
          causationId: `availability-${variantId}`
        }
      );
      let latestCart = null;
      orchestrator.execute(command, {
        identifyAffectedSurfaces: () => ({
          ok: true,
          surfaces: [...documentRef.querySelectorAll('[data-inventory-surface]')]
            .map((surface) => surface.dataset.inventorySurface)
            .filter(Boolean)
            .slice(0, 24)
        }),
        revalidateCartMembership: async () => {
          latestCart = await browserRoot.CorkopiaMyBox?.refresh?.({ validate: false }) || null;
          return { ok: true, authority: 'shopify', cartRead: Boolean(latestCart) };
        },
        handleInvalidatedSubject: () => {
          const carted = Boolean(latestCart?.items?.some((item) => String(item.variant_id) === variantId));
          documentRef.dispatchEvent(new CustomEvent('corkopia:inventory-subject-invalidated', {
            detail: { variantId, pieceId, carted, destructiveAction: false }
          }));
          return { ok: true, carted, destructiveAction: false };
        },
        requestCurrentReplacement: () => ({
          ok: true,
          replacement: null,
          source: 'current_dynamic_inventory_only'
        }),
        refreshStorefrontSurfaces: () => {
          documentRef.dispatchEvent(new CustomEvent('corkopia:catalog-change', {
            detail: {
              reason: 'INVENTORY_INVALIDATED',
              variantId,
              orchestrated: true,
              futureInventoryIncluded: false
            }
          }));
          return { ok: true, bounded: true };
        },
        refreshAdvisors: () => {
          documentRef.dispatchEvent(new CustomEvent('corkopia:orchestrator-step-requested', {
            detail: { name: 'ADVISORS_REFRESHED', variantId }
          }));
          return { ok: true };
        }
      }).catch(() => {});
    });
    return true;
  };

  return Object.freeze({
    VERSION,
    CONTRACT_VERSION,
    STATES,
    COMMANDS,
    EVENTS,
    WORKFLOWS,
    DEFINITIONS,
    createCommand,
    validateCommand,
    validateEvent,
    businessKey,
    createOrchestrator,
    bindBrowser,
    sanitize
  });
});
